import { generatePath, useParams } from 'react-router-dom';
import { TabList, TabPanel, TabPanelBody, Tabs } from 'components/tabs';
import {
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_ADVICE,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_GLOBAL,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_GLOBAL_EDIT,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_REPORT,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_REPORT_TESTDATA,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS_TESTDATA,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS_TESTDATA_EDIT
} from 'routes/RouteList';
import { FormattedMessage } from 'react-intl';
import messages from 'messages';
import Badge from 'components/badge/Badge';
import { useRouteMatch } from 'react-router-dom';
import useTabIndex from 'hooks/utils/useTabIndex';
import { useTestDataContext } from 'contexts/TestDataContext';
import TabLink from 'components/tabs/TabLink';
import slugify from 'slugify';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import SessionPersonReportTab from 'containers/pages/sessions/SessionPersonReportTab';
import SessionPersonDataTab from 'containers/pages/sessions/SessionPersonDataTab';
import SessionPersonGlobalTab from 'containers/pages/sessions/SessionPersonGlobalTab';
import SessionPersonAdviceTab from 'containers/pages/sessions/SessionPersonAdviceTab';
import { useSessionContext } from 'contexts/SessionContext';

const SessionPersonTabs = () => {
  const ability = useAbility(AbilityContext);
  const { entityId, sessionsTab, sessionId, personId, testDataId, programId } =
    useParams();
  const { session } = useSessionContext();
  const { hasResults } = useTestDataContext();
  const isAdviceView = useRouteMatch(
    ROUTE_SESSIONS_ENTITY_SESSION_PERSON_ADVICE
  );

  let tabRoutes = [
    [
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_REPORT,
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_REPORT_TESTDATA
    ]
  ];

  if (ability.can('update', 'TestData') && session.isTestSetV2) {
    tabRoutes.push([
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS,
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS_TESTDATA,
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS_TESTDATA_EDIT
    ]);
  }
  tabRoutes.push([
    ROUTE_SESSIONS_ENTITY_SESSION_PERSON_GLOBAL,
    ROUTE_SESSIONS_ENTITY_SESSION_PERSON_GLOBAL_EDIT
  ]);
  if (ability.can('manage', 'Sessions')) {
    tabRoutes.push(ROUTE_SESSIONS_ENTITY_SESSION_PERSON_ADVICE);
  }

  const [tabIndex, setTabIndex] = useTabIndex(tabRoutes);

  return (
    <Tabs
      selectedIndex={tabIndex}
      onSelect={index => setTabIndex(index)}
      fullWidth={isAdviceView}
    >
      <TabList>
        {ability.can('read', 'TestData') && (
          <TabLink
            key="session.person.report"
            to={
              testDataId
                ? generatePath(
                    ROUTE_SESSIONS_ENTITY_SESSION_PERSON_REPORT_TESTDATA,
                    {
                      entityId,
                      sessionsTab,
                      sessionId,
                      personId,
                      testDataId
                    }
                  )
                : generatePath(ROUTE_SESSIONS_ENTITY_SESSION_PERSON_REPORT, {
                    entityId,
                    sessionsTab,
                    sessionId,
                    personId
                  })
            }
            disabled={!hasResults()}
            dataQa={`person-${slugify(messages.tabSessionSporterReport.id).toLowerCase()}`}
          >
            <FormattedMessage {...messages.tabSessionSporterReport} />
          </TabLink>
        )}
        {ability.can('update', 'TestData') && session.isTestSetV2 && (
          <TabLink
            key="session.person.data"
            to={
              testDataId
                ? generatePath(
                    ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS_TESTDATA,
                    {
                      sessionId,
                      sessionsTab,
                      entityId,
                      personId,
                      testDataId
                    }
                  )
                : generatePath(ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS, {
                    entityId,
                    sessionsTab,
                    sessionId,
                    personId
                  })
            }
            dataQa={`person-${slugify(messages.tabSessionSporterTests.id).toLowerCase()}`}
          >
            <FormattedMessage {...messages.tabSessionSporterTests} />
          </TabLink>
        )}

        <TabLink
          key="session.person.global"
          to={generatePath(ROUTE_SESSIONS_ENTITY_SESSION_PERSON_GLOBAL, {
            entityId,
            sessionsTab,
            sessionId,
            personId
          })}
          dataQa={`person-${slugify(messages.tabSessionGlobal.id).toLowerCase()}`}
        >
          <FormattedMessage {...messages.tabSessionGlobal} />
        </TabLink>
        {isAdviceView && programId && ability.can('manage', 'Sessions') && (
          <TabLink
            key="session.person.advice"
            to={generatePath(ROUTE_SESSIONS_ENTITY_SESSION_PERSON_ADVICE, {
              entityId,
              sessionsTab,
              sessionId,
              personId,
              programId
            })}
            dataQa={`person-${slugify(messages.badgeSessionSporterAdvice.id).toLowerCase()}`}
          >
            <Badge>
              <FormattedMessage {...messages.badgeSessionSporterAdvice} />
            </Badge>
            <FormattedMessage {...messages.tabSessionSporterAdvice} />
          </TabLink>
        )}
      </TabList>

      {/* Tab > REPORT */}
      {ability.can('read', 'TestData') && (
        <TabPanel key="session.sporter.report">
          <TabPanelBody withScroller>
            <SessionPersonReportTab />
          </TabPanelBody>
        </TabPanel>
      )}

      {/* Tab > DATA */}
      {ability.can('update', 'TestData') && session.isTestSetV2 && (
        <TabPanel key="session.person.data">
          <TabPanelBody withScroller>
            <SessionPersonDataTab />
          </TabPanelBody>
        </TabPanel>
      )}

      {/* Tab > GLOBAL */}
      <TabPanel key="session.person.global">
        <TabPanelBody withScroller>
          <SessionPersonGlobalTab />
        </TabPanelBody>
      </TabPanel>

      {/* Tab > ADVICE */}
      {isAdviceView && ability.can('manage', 'Sessions') && (
        <TabPanel key="session.person.advice">
          <SessionPersonAdviceTab />
        </TabPanel>
      )}
    </Tabs>
  );
};

export default SessionPersonTabs;
